/**
 * Home page
 */

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Grid } from '@mui/material';
import Button from '@mui/material/Button';
import AppReminder from 'components/AppReminder';
import AppStoreLinksMiniBanner from 'components/AppStoreLinksMiniBanner/AppStoreLinksMiniBanner';
import BackToTopButton from 'components/BackToTopButton/BackToTopButton';
import CategoryNav from 'components/CategoryNav/CategoryNav';
import Dac7Modal from 'components/Dac7Modal';
import InfiniteScroll from 'components/InfiniteScroll/InfiniteScroll';
import Loader from 'components/Loader/Loader';
import PaymentShieldHighlighted from 'components/PaymentShieldHighlighted/PaymentShieldHighlighted';
import config, { Country } from 'config';
import { CATEGORY, CATEGORY_PRODUCTS, MY, PRODUCT, PRODUCT_SEARCH, SEARCH, SHOP, SHOPS } from 'config/fe-routes';
import moment from 'moment';
import { useRouter } from 'next/router';
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProductService from 'services/product.service';
import { RecsysTracking } from 'services/recsysTracking.service';
import { userPromoCodeService } from 'services/user-promo-code.service';
import { openLoginModal } from 'store/actions/login.actions';
import { brandsChanged } from 'store/actions/search.actions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { HomeFeedEngine, ProductType } from 'types/product.types';
import ImageUrlResizer from 'utils/image-url-resizer';
import { isObjectEmptyByKey } from 'utils/object-helpers';
import { savePendingReferralCode } from 'utils/promo-code';
import { redirectTo } from 'utils/route-helpers';

import { useFireOnFirstInView } from '../../hooks/useFireOnFirstInView';
import useGlobalContext from '../../hooks/useGlobalContext';
import { useLanguage } from '../../hooks/useLanguage';
import { usePriceDropBadgeListView } from '../../hooks/usePriceDropBadgeTracking';
import { GTM, GTMLocations } from '../../utils/analytics';
import { useHomePageCmsData } from '../../utils/cms/useHomePageCmsData';
import Banner from '../Banner';
import BrandItem from '../Brand';
import BrandsModal from '../Brand/BrandsModal/BrandsModal';
import BlockImage from '../layout/BlockImages/BlockImage/BlockImage';
import BlockImages from '../layout/BlockImages/BlockImages';
import BlockShop from '../layout/BlockShop/BlockShop';
import BlockTitle from '../layout/BlockTitle/BlockTitle';
import PaymentsShippingLogos from '../PaymentsShippingLogos/PaymentsShippingLogos';
import ScrollBox from '../ScrollBox/ScrollBox';
import SellButton from '../SellButton/SellButton';

const productService = new ProductService();

const PRODUCT_FEED_LIST_ID = 'homescreen_product_feed';
const FEATURED_SHOP_LIST_ID = 'homescreen_featured_shop';
const campaignListId = (campaignId: number | string) => `homescreen_campaign_${campaignId}`;

const ITEMS_PER_ROW = 6;
const PRODUCTS_PER_PAGE = ITEMS_PER_ROW * 5;

const DEFAULT_BRAND_ROWS = 2;
const DEFAULT_BRANDS_PER_ROW = 8;

export const useDropHomePageCache = () => {
  const { set } = useGlobalContext();

  return () =>
    set({
      homePageCacheKey: 'reset',
      homePageProducts: [],
      hasMoreHomePageProducts: true,
      homePageCampaignProductsActive: false,
      homePageFeedEngine: undefined,
    });
};

const HomePage = () => {
  const { t } = useTranslation();
  const { get, set } = useGlobalContext();
  const buyerProtectionHighlighted = config.get('buyerProtectionHighlighted');

  const { user, isLoginLoading } = useAppSelector(({ userData }) => ({ user: userData.user, isLoginLoading: userData.isLoading }));
  const { country } = useLanguage();

  const dispatch = useAppDispatch();

  const shouldShowNewListerBadge = (product: ProductType) => product?.shop?.isNewLister || product?.isNewLister;

  const { activeBanner, featuredShops, campaignsProducts, featuredBrands, brands, homepageBrands, productFilters } = useHomePageCmsData();

  const setCacheKey = (homePageCacheKey) => set({ homePageCacheKey });
  const cacheKey = get('homePageCacheKey') as string;
  const isCacheApplied = getNewCacheKey() === cacheKey;

  const products = isCacheApplied ? ((get('homePageProducts') as any[]) ?? []) : [];

  const setProducts = (homePageProducts) => set({ homePageProducts });

  const hasMoreProducts = isCacheApplied ? (get('hasMoreHomePageProducts') as boolean) : true;
  const setHasMoreProducts = (hasMoreHomePageProducts) => set({ hasMoreHomePageProducts });

  const campaignProductsActive = isCacheApplied ? (get('homePageCampaignProductsActive') as boolean) : false;
  const setCampaignProductsActive = (homePageCampaignProductsActive) => set({ homePageCampaignProductsActive });

  const [wasCachedOnInit, setWasCachedOnInit] = useState(isCacheApplied);
  const [brandsModalOpened, setBrandsModalOpened] = useState<boolean>(false);
  const ITEMS_PER_INCOMPLETE_ROW = productFilters?.length % ITEMS_PER_ROW;

  const initSegment = () => {
    if (campaignsProducts?.length > 0) {
      let segmentProducts = [];

      campaignsProducts.forEach((campaign) => {
        segmentProducts = [...segmentProducts, ...campaign.products];
      });
    }
  };

  const router = useRouter();
  const referralCode = router.query['referral-code']?.toString();

  useEffect(() => {
    if (!referralCode || isLoginLoading) {
      return;
    }

    if (user?.isLoggedIn) {
      userPromoCodeService.registerFromInviteLink(referralCode);
      redirectTo(MY.PROMOCODES(), true);

      return;
    }

    savePendingReferralCode(referralCode);
    openLoginModal(true, MY.PROMOCODES(), referralCode);
  }, [referralCode, isLoginLoading]);

  function getNewCacheKey(): string {
    return 'home';
  }

  // Get products
  const loadMoreProducts = async (page: number, initialProducts: any[] = products) => {
    let nextProducts, total;
    // Request next set of products

    try {
      // Feed semantics (statuses, pinning, CMS category scoping) are server-owned — only pagination is sent.
      const response = await productService.getHomeFeed({
        offset: PRODUCTS_PER_PAGE * page,
        limit: PRODUCTS_PER_PAGE,
        firstProductId: getFirstNonPinnedProductId(initialProducts) || null,
      });

      // recommendation_provider is assigned server-side — fire its exposure here, once per feed load.
      if (page === 0 && response?.experiment?.inExperiment && response.experiment.experimentId && response.experiment.variationId) {
        GTM.experimentViewed({
          experimentId: response.experiment.experimentId,
          variationId: response.experiment.variationId,
        });
      }

      if (page === 0) {
        GTM.bumpedItemsViewed(response?.bumpedItems);
      }

      // Feed title follows the provider that actually served the first page (fallbacks included).
      if (page === 0) {
        set({ homePageFeedEngine: response?.recommendationEngine });
      }

      nextProducts = response.list;
      total = response.total;

      // Each feed page is its own /recommend response with its own attribution id and its own
      // serving engine (a provider failure on any page falls back per request). Both ride on the
      // product so they survive the products cache (a card restored from cache on back-navigation
      // must still attribute its click) and so paging never re-attributes earlier cards.
      nextProducts = nextProducts?.map((product) => ({
        ...product,
        recommendationId: response.recommendationId,
        recommendationEngine: response.recommendationEngine,
      }));

      // Combine products
      const combinedProducts = initialProducts.concat(nextProducts || []);

      // Check for more
      setHasMoreProducts(!!nextProducts?.length && total > combinedProducts.length);

      // Save new products to store
      setProducts(combinedProducts);
    } catch (e) {
      // Do nothing
    }

    // After we load one page we can safely assume that next loaded items are not part of cache.
    // This will enable blockimage loader and lazyness.
    setWasCachedOnInit(false);
  };

  function getFirstNonPinnedProductId(productsList) {
    const nonPinnedProductId = productsList.find((p) => !p.pinnedAt);

    return nonPinnedProductId?.id;
  }

  const generateHomepageBrands = (brandsSource) =>
    brandsSource.map((brand) => <BrandItem key={brand.id} brand={brand} globalLoaderEnabled={true} />);

  const generateHomepageBrandsRows = () => {
    const rows = homepageBrands.rows.numberOfRows || DEFAULT_BRAND_ROWS;
    const itemsInRow = homepageBrands.rows.brandsInRow || DEFAULT_BRANDS_PER_ROW;
    const rowsItems = [];

    for (let i = 1; i <= rows; i += 1) {
      const startIndex = (i - 1) * itemsInRow;
      const lastIndex = i * itemsInRow;

      rowsItems.push(brands.slice(startIndex, lastIndex));
    }

    return rowsItems.map((row, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <div key={`brand-row-${index}`} className="brand-item__row">
        {generateHomepageBrands(row)}
      </div>
    ));
  };

  useEffect(() => {
    initSegment();

    if (campaignsProducts?.length > 0) {
      const campaignActiveStatus = campaignsProducts?.some((campaign) => {
        const hasEmptyProducts = campaign?.products.some((product) => isObjectEmptyByKey(product));

        return campaign.active && campaign?.products?.length > 0 && !hasEmptyProducts;
      });

      setCampaignProductsActive(campaignActiveStatus);
    }
  }, [campaignsProducts, cacheKey]);

  useEffect(() => {
    if (localStorage.getItem('last_checkout_slug')) {
      localStorage.removeItem('last_checkout_slug');
    }

    if (localStorage.getItem('last_checkout_data')) {
      localStorage.removeItem('last_checkout_data');
    }
  }, []);

  // Fire view_item_list for Featured Shop products once when its data is available.
  // The section sits high on the page; firing on data-available is a close enough
  // proxy for visibility for now.
  const featuredShopViewedRef = useRef(false);

  useEffect(() => {
    const products = featuredShops?.shop?.products;

    if (featuredShopViewedRef.current || !products?.length) {
      return;
    }
    featuredShopViewedRef.current = true;
    GTM.viewItemList(products, FEATURED_SHOP_LIST_ID);
  }, [featuredShops?.shop?.products]);

  // Fire view_item_list once per Campaign Products section.
  const firedCampaignsRef = useRef<Set<number | string>>(new Set());

  useEffect(() => {
    if (!campaignsProducts?.length) {
      return;
    }
    campaignsProducts.forEach((campaign) => {
      if (!campaign?.active || firedCampaignsRef.current.has(campaign.id) || !campaign?.products?.length) {
        return;
      }
      firedCampaignsRef.current.add(campaign.id);
      GTM.viewItemList(campaign.products, campaignListId(campaign.id));
    });
  }, [campaignsProducts]);

  const firedFeedPagesRef = useRef<Set<number>>(new Set());

  const fireFeedViewItemList = useCallback(
    (page: number) => {
      if (firedFeedPagesRef.current.has(page)) {
        return;
      }

      const start = page * PRODUCTS_PER_PAGE;
      const pageItems = products?.slice(start, start + PRODUCTS_PER_PAGE);

      if (!pageItems?.length) {
        return;
      }
      firedFeedPagesRef.current.add(page);
      GTM.viewItemList(pageItems, PRODUCT_FEED_LIST_ID, start, { search_engine: pageItems[0]?.recommendationEngine });
    },
    [products],
  );

  const productFeedRef = useFireOnFirstInView(() => fireFeedViewItemList(0), !!products?.length, 0);
  const priceDropTracking = usePriceDropBadgeListView(PRODUCT_FEED_LIST_ID);

  useEffect(() => {
    const totalPages = Math.ceil((products?.length ?? 0) / PRODUCTS_PER_PAGE);

    for (let p = 1; p < totalPages; p++) {
      fireFeedViewItemList(p);
    }
  }, [products?.length, fireFeedViewItemList]);

  // Set cache on load.
  const infiniteScrollBeforeEachLoad = ({ reset }) => {
    const newProductCacheKey = getNewCacheKey();

    // If url changed (cache key), make new request.
    if (newProductCacheKey !== cacheKey) {
      setCacheKey(newProductCacheKey);
      reset();
      setProducts([]);
      loadMoreProducts(0, []);
      setHasMoreProducts(true);
      setCampaignProductsActive(false);
      window.scrollTo(0, 0);

      // Stop loading next page this time.
      return true;
    }
  };

  const getDac7Modal = () => {
    if (typeof window === 'undefined') {
      return;
    }

    const lastOpenAt = localStorage.getItem('dac7ModalLastOpenAt');

    if (country === Country.EE && (!lastOpenAt || moment().diff(moment(lastOpenAt), 'days') >= 30)) {
      return <Dac7Modal />;
    }
  };

  const feedEngine = get('homePageFeedEngine');
  const isRecencyFeed = !feedEngine || feedEngine === 'legacy-postgres';

  return (
    <div className="home">
      <AppReminder />

      {getDac7Modal()}

      {/* CATEGORY NAVIGATION */}
      <CategoryNav GTMLocation={GTMLocations.HOME_PAGE} />

      {/* Buyer protection banner */}
      {buyerProtectionHighlighted && (
        <div className="banner-top">
          <PaymentShieldHighlighted />
        </div>
      )}

      {/* BANNER */}
      <Banner banner={activeBanner} />

      {/* show copyright if configured */}
      {config.copyright && (
        <div className="copyright">
          <span>
            {config.copyright} {new Date().getFullYear()}
          </span>
        </div>
      )}

      <div className="page-wide">
        {/* HOMEPAGE BRANDS */}
        {homepageBrands?.active && !!brands?.length && (
          <div className="brands__wrapper pb-6 pt-2">
            <Grid container justifyContent="space-between" alignItems="center">
              <BlockTitle mb={2}>
                <i className="icon yaga-tag block-title-icon" />
                {homepageBrands?.title}
              </BlockTitle>

              <span tabIndex={0} className="title__action" role="button" onClick={() => setBrandsModalOpened(true)}>
                {t('pages.index.see-more')}
              </span>
            </Grid>

            <ScrollBox>{generateHomepageBrandsRows()}</ScrollBox>

            <BrandsModal
              open={brandsModalOpened}
              title={homepageBrands?.title}
              icon="yaga-tag"
              close={() => setBrandsModalOpened(false)}
              brands={homepageBrands?.brands}
            />
          </div>
        )}

        {/* FEATURED SHOPS */}
        {featuredShops?.shop && (
          <Grid className="featured-shops pb-6">
            <BlockTitle>
              <i className="icon yaga-shop block-title-icon" />
              {featuredShops?.title}
            </BlockTitle>

            <BlockShop
              name={featuredShops?.shop?.name}
              slug={featuredShops?.shop?.activeSlug}
              imageSrc={featuredShops?.shop?.avatar?.original}
              globalLoaderEnabled={true}
              {...SHOP(featuredShops?.shop?.activeSlug)}
            />

            <BlockImages>
              {featuredShops?.shop?.products?.map((product, index) => {
                const productImage = product?.images?.[0];

                return (
                  <BlockImage
                    key={'featured-shop-key-' + (productImage?.title || productImage?.thumbnail)}
                    productImage={productImage}
                    alt={productImage?.title}
                    price={product?.price}
                    brand={product?.brand}
                    tagged={product?.tagged}
                    status={product?.status}
                    globalLoaderEnabled={true}
                    showImageLoader={!wasCachedOnInit}
                    disableLazy={wasCachedOnInit}
                    {...PRODUCT(featuredShops?.shop?.activeSlug, product.slug, undefined, index, FEATURED_SHOP_LIST_ID)}
                    onClick={() => GTM.selectItem(product, FEATURED_SHOP_LIST_ID, index)}
                    isNewLister={shouldShowNewListerBadge(product)}
                    isCharity={product?.tags?.includes('Charity') || product?.isCharityProduct}
                  />
                );
              })}
            </BlockImages>

            <Grid container justifyContent="flex-end" className="mt-4">
              <Button variant="contained" color="primary" size="medium" onClick={() => redirectTo(SHOPS())} className="mobile-full-width">
                {t('pages.index.more-shops')}
              </Button>
            </Grid>
          </Grid>
        )}

        {/* FEATURED LINKS */}

        {!!featuredBrands?.featuredLinks?.length && (
          <div className="featured-links pb-6">
            <BlockTitle>
              <i className="icon block-title-icon">
                <img src="/icons/icn-hot.svg" alt="" className="custom-icon" />
              </i>
              {featuredBrands?.title}
            </BlockTitle>

            <BlockImages>
              {featuredBrands?.featuredLinks?.map((brand) => (
                <BlockImage
                  key={'featured-brand-key-' + brand.link}
                  src={ImageUrlResizer.resize(brand?.imageURL, 300, true)}
                  alt={brand.link}
                  globalLoaderEnabled={true}
                  showImageLoader={!wasCachedOnInit}
                  disableLazy={wasCachedOnInit}
                  onClick={() => {
                    dispatch(brandsChanged([brand.id]));
                  }}
                  {...PRODUCT_SEARCH(brand.link)}
                />
              ))}
            </BlockImages>
          </div>
        )}
      </div>

      {/* FAVOURITES TODAY */}
      {campaignProductsActive &&
        campaignsProducts?.map(
          (campaign, index) =>
            campaign.active && (
              <div
                key={campaign?.id}
                className={`pt-2 banner-width favorites-block
                  ${index % 2 !== 0 ? 'favorites-block--white' : ''}
                  ${index === campaignsProducts?.length - 1 && index % 2 !== 0 ? 'pb-0' : 'pb-8'}
                `}
              >
                <div className="page-wide favorites-block-content">
                  <BlockTitle>
                    <i className="icon yaga-favorite block-title-icon" />
                    {campaign?.title}
                  </BlockTitle>

                  <BlockImages>
                    {campaign?.products
                      ?.filter((cProduct) => !isObjectEmptyByKey(cProduct))
                      .filter((cProduct) => cProduct.status !== 'deleted' && cProduct.status !== 'not-visible')
                      .map((product, index) => (
                        <BlockImage
                          key={'favorite-today-key-' + product.id}
                          productImage={product.images?.[0]}
                          alt={product.images?.[0]?.fileName}
                          status={product?.status}
                          price={product.price}
                          details={product.size}
                          brand={product.brand}
                          tagged={product.tagged}
                          globalLoaderEnabled={true}
                          productId={product.id}
                          shopId={product.shopId}
                          likeCount={product.likeCount}
                          showImageLoader={!wasCachedOnInit}
                          disableLazy={wasCachedOnInit}
                          {...PRODUCT(product?.shop?.activeSlug, product.slug, undefined, index, campaignListId(campaign.id))}
                          onClick={() => GTM.selectItem(product, campaignListId(campaign.id), index)}
                          isCharity={product?.tags?.includes('Charity') || product?.isCharityProduct}
                          isNewLister={shouldShowNewListerBadge(product)}
                        />
                      ))}
                  </BlockImages>
                </div>
              </div>
            ),
        )}

      {/* PAYMENTS AND SHIPPING LOGOS */}
      <PaymentsShippingLogos />

      {/* PRODUCTS */}

      {!!productFilters?.length && (
        <div className="page-wide mt-8">
          <BlockTitle>{t('pages.index.categories')}</BlockTitle>
          <BlockImages>
            {/* categories */}
            {productFilters?.map((filter) => (
              <BlockImage
                key={'filter-' + filter.name}
                src={filter.image}
                alt={filter.name}
                textOver={filter.name}
                dark
                blurhash={false}
                globalLoaderEnabled={true}
                {...(filter.defaultView === 'products' ? CATEGORY_PRODUCTS(filter.feEndpoint) : CATEGORY(filter.feEndpoint))}
              />
            ))}
          </BlockImages>
        </div>
      )}

      <div className="page-wide mt-8" ref={productFeedRef}>
        <Grid container justifyContent="space-between" alignItems="center">
          <BlockTitle>{isRecencyFeed ? t('pages.index.new-items') : t('pages.index.for-you')}</BlockTitle>
          {isRecencyFeed && (
            <span tabIndex={0} className="title__action title__action--filter" role="button" onClick={() => redirectTo(SEARCH(undefined))}>
              {t('common.filter')} <ArrowForwardIcon className="filter-button-caret" />
            </span>
          )}
        </Grid>
        <BlockImages>
          {/* products */}
          <InfiniteScroll
            batchSize={PRODUCTS_PER_PAGE}
            dataLength={products.length}
            loadMore={(page) => loadMoreProducts(page)}
            hasMore={hasMoreProducts}
            loader={<Loader mb={16} />}
            manualLoadFirstSet
            beforeEachLoad={infiniteScrollBeforeEachLoad}
          >
            {(hasMoreProducts ? products.slice(0, products.length - ITEMS_PER_INCOMPLETE_ROW) : products).map((product, index) => (
              <Fragment key={'product-' + product.slug}>
                {index === 10 && user?.isLoggedIn && !user?.isAppUser && (
                  <AppStoreLinksMiniBanner GTMLocation={GTMLocations.HOME_PAGE} linksLocation="App Store links mini banner" />
                )}
                <BlockImage
                  productImage={product.images?.[0]}
                  isCharity={product?.tags?.includes('Charity') || product?.isCharityProduct}
                  alt={product.images?.[0]?.fileName}
                  price={product.price}
                  priceDrop={product.priceDrop}
                  productSlug={product.slug}
                  priceDropTracking={priceDropTracking}
                  status={product.status}
                  details={product?.attributes?.size}
                  brand={product.brand}
                  tagged={product.tagged}
                  pinned={!!product.pinnedAt}
                  globalLoaderEnabled={true}
                  productId={product.id}
                  shopId={product.shopId}
                  likeCount={product.likeCount}
                  isOwner={product.isOwner}
                  bumped={product.bumped}
                  recommendationId={product.recommendationId}
                  recommendationEngine={product.recommendationEngine ?? (get('homePageFeedEngine') as HomeFeedEngine | undefined)}
                  showImageLoader={!wasCachedOnInit}
                  disableLazy={wasCachedOnInit}
                  isNewLister={shouldShowNewListerBadge(product)}
                  {...PRODUCT(
                    product?.shop?.activeSlug,
                    product.slug,
                    undefined,
                    index,
                    PRODUCT_FEED_LIST_ID,
                    undefined,
                    product.recommendationId,
                    product.recommendationEngine,
                  )}
                  onClick={() => {
                    const feedEngine = product.recommendationEngine ?? (get('homePageFeedEngine') as HomeFeedEngine | undefined);

                    GTM.selectItem(product, PRODUCT_FEED_LIST_ID, index, { search_engine: feedEngine });

                    if (product?.id && product?.slug) {
                      RecsysTracking.productSelect({
                        product: { id: product.id, slug: product.slug },
                        recommendationId: product.recommendationId,
                        recommendationEngine: feedEngine,
                        rank: index + 1,
                      });
                    }
                  }}
                />
              </Fragment>
            ))}
          </InfiniteScroll>
        </BlockImages>
      </div>

      {/* fixed sell button in the bottom */}
      <SellButton />

      <BackToTopButton location={GTMLocations.HOME_PAGE} />
    </div>
  );
};

export default HomePage;
